import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, MapPin, Linkedin, Twitter, Facebook, Instagram, Youtube, Globe } from "lucide-react";
import { useSite } from "@/contexts/SiteContent";
import { resolveAssetUrl } from "@/lib/api";
import RichText from "@/components/RichText";

const ICONS = { Linkedin, Twitter, Facebook, Instagram, Youtube, Globe };

export default function Footer() {
  const { content } = useSite();
  const f = content?.footer || {};
  const brand = content?.branding || {};
  const contact = f.contact || {};
  const wa = (contact.whatsapp || "").replace(/\D/g, "");
  const logoUrl = resolveAssetUrl(brand.logo_url || "");

  return (
    <footer className="bg-navy text-slate-200 mt-24 relative overflow-hidden">
      <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-saffron/15 blur-3xl"/>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              {logoUrl && <img src={logoUrl} alt={brand.site_name || "TARA"} style={{ height: `${brand.logo_height || 44}px` }} className="w-auto object-contain"/>}
              <div>
                <div className="text-[1rem] sm:text-[1.05rem] font-extrabold tracking-[0.04em]">
                  {(() => {
                    const name = (brand.site_name || "TARA Finserv").trim();
                    const parts = name.split(/\s+/);
                    const first = parts.shift() || "";
                    const rest = parts.join(" ");
                    return (<>
                      <span className="text-white">{first}</span>
                      {rest && <> <span className="text-saffron">{rest}</span></>}
                    </>);
                  })()}
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/60 mt-1">{brand.tagline || "DSA · Loans · Advisory"}</div>
              </div>
            </div>
            <RichText html={f.blurb} variant="footer" className="mt-5 max-w-sm"/>
            <div className="mt-6 flex gap-3">
              {(f.socials || []).map((s, i) => {
                const Ico = ICONS[s.icon] || Globe;
                return (
                  <a key={i} href={s.url || "#"} target={s.url?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                     data-testid={`footer-social-${(s.label||"x").toLowerCase()}`}
                     className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-saffron transition-colors" aria-label={s.label}>
                    <Ico className="h-4 w-4"/>
                  </a>
                );
              })}
            </div>
          </div>

          {(f.columns || []).map((col, i) => (
            <div key={i} className="md:col-span-2">
              <div className="overline text-saffron">{col.heading}</div>
              <ul className="mt-4 space-y-2.5 text-sm">
                {(col.links || []).map((l, j) => (
                  <li key={j}>
                    {l.url?.startsWith("http") ?
                      <a href={l.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white">{l.label}</a> :
                      <Link to={l.url || "#"} className="text-slate-400 hover:text-white">{l.label}</Link>}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-4">
            <div className="overline text-saffron">Get in Touch</div>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              {contact.phone && <li className="flex gap-3"><Phone className="h-4 w-4 mt-0.5 text-saffron" strokeWidth={1.8}/><a href={`tel:${contact.phone.replace(/\s/g,'')}`} data-testid="footer-phone" className="hover:text-white">{contact.phone}</a></li>}
              {contact.email && <li className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 text-saffron" strokeWidth={1.8}/><a href={`mailto:${contact.email}`} data-testid="footer-email" className="hover:text-white">{contact.email}</a></li>}
              {wa && <li className="flex gap-3"><MessageCircle className="h-4 w-4 mt-0.5 text-saffron" strokeWidth={1.8}/><a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="hover:text-white">WhatsApp chat</a></li>}
            </ul>
            {(f.offices || []).length > 0 && (
              <div className="mt-6 space-y-3">
                {(f.offices || []).map((o, i) => (
                  <div key={i} className="text-sm">
                    <div className="overline text-saffron">{o.heading}</div>
                    <div className="flex gap-3 mt-2"><MapPin className="h-4 w-4 mt-0.5 text-saffron shrink-0" strokeWidth={1.8}/><span className="text-slate-300">{o.address}</span></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-400">
          <div>{f.copyright || "© 2026 TARA Finserv."}</div>
          <div className="flex gap-5 items-center flex-wrap">
            {(f.policy_links || []).map((p, i) => (
              <Link key={i} to={p.url || "#"} className="hover:text-white">{p.label}</Link>
            ))}
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-saffron">Admin</Link>
            <Link to="/agent/login" data-testid="footer-agent-link" className="hover:text-saffron">Agent Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
